// Table block — pure model + grid operations (no React, no craft, no redux).
// ISP-4018 (Fable Hail Mary Wave 5). Deck: concepts/builder-blocks/02-table-block-editor.
//
// WHY A PURE MODULE
// The novel, corruption-prone logic of the Table block is the GRID: adding /
// removing rows & columns must keep every row, the header, and the per-column
// alignment array the SAME length (numCols) or the rendered <table> and the
// editor desync. Keeping that logic here — as pure, immutable, unit-tested
// functions — means the craft.js settings adapter and the presentational editor
// share ONE contract that a single test file can pin, exactly like the WL-011b
// Container/Image/Text `wiring.js` modules.
//
// IMMUTABILITY: every op returns a NEW value (fresh arrays/objects, no in-place
// mutation) so craft.js `setProp` sees a real change and undo/redo snapshots stay
// independent. Callers pass the current value in and get the next value out.
//
// ALIGN values: "left" | "center" | "right" (right = numeric columns per deck).

export const ALIGNS = ["left", "center", "right"];

// Deck default: a 3-column × 6-row Career table with a purple header bar.
export const DEFAULT = {
  showHeader: true,
  header: ["Role", "Where", "Year"],
  rows: [
    ["UX Lead", "Contoso", "2024"],
    ["Product Designer", "Northwind", "2021"],
    ["Design Intern", "Fabrikam", "2018"],
    ["Freelance UX", "Studio North", "2016"],
    ["QA Analyst", "Initech", "2014"],
    ["Support Lead", "Umbrella", "2012"],
  ],
  aligns: ["left", "left", "right"],
  caption: "",
  font: "DM Sans",
  fontColor: "#123051", // tokens.field.ink (navy)
  // ISP-9999: default the header bar to the segment color (suggested_color_0),
  // like every other color-controllable block (Buttons/TitleBar/Container/...).
  // Resolved to a real hex by Table.jsx's usePaletteColor before it ever
  // reaches TableView; a user override overwrites this with a literal hex.
  headerColor: "suggested_color_0",
  visibleRows: 4, // 4 rows show in the Segment; the rest scroll (deck rule)
};

// fix-kelly-0930-P0 (Kelly, VP Sales, 2026-09-30): "Newly added Graph and
// Table blocks must start EMPTY (no demo rows 'UX Lead/Contoso', no demo
// bars) -- show a clean empty state with a hint to add data." DEFAULT above
// is kept UNCHANGED as normalize()'s fallback for legacy/malformed values
// (existing saved Tables that round-trip through here must not change); EMPTY
// is the real starting point for a block the user just dragged in -- see
// Table.jsx's TableDefaultProps. TableView.jsx renders a "hint to add data"
// placeholder whenever every header/cell is blank, mirroring GraphChart.jsx's
// existing "Add data points to see your chart" empty state.
export const EMPTY = {
  showHeader: true,
  header: [""],
  rows: [[""]],
  aligns: ["left"],
  caption: "",
  font: "DM Sans",
  fontColor: "#123051",
  headerColor: "suggested_color_0",
  visibleRows: 4,
};

const MAX_COLS = 12;
const MAX_ROWS = 100;
const MIN_COLS = 1;
const MIN_ROWS = 1;

// ── helpers ──────────────────────────────────────────────────────────────────
const clone2d = (rows) => rows.map((r) => r.slice());
const numCols = (v) => v.header.length;

// Coerce any partial/legacy value into a fully-consistent model (every row,
// the header and aligns are exactly numCols long). Safe to call on unknown input.
export function normalize(value) {
  const v = { ...DEFAULT, ...(value || {}) };
  let header = Array.isArray(v.header) ? v.header.map((c) => (c == null ? "" : String(c))) : [...DEFAULT.header];
  if (header.length < MIN_COLS) header = [...DEFAULT.header];
  const cols = header.length;
  let rows = Array.isArray(v.rows) && v.rows.length ? v.rows.map((r) => (Array.isArray(r) ? r.slice() : [])) : clone2d(DEFAULT.rows);
  rows = rows.map((r) => {
    const nr = r.slice(0, cols).map((c) => (c == null ? "" : String(c)));
    while (nr.length < cols) nr.push("");
    return nr;
  });
  if (rows.length < MIN_ROWS) rows = [new Array(cols).fill("")];
  let aligns = Array.isArray(v.aligns) ? v.aligns.slice(0, cols) : [];
  while (aligns.length < cols) aligns.push("left");
  aligns = aligns.map((a) => (ALIGNS.includes(a) ? a : "left"));
  return {
    showHeader: v.showHeader !== false,
    header,
    rows,
    aligns,
    caption: v.caption == null ? "" : String(v.caption),
    font: v.font || DEFAULT.font,
    fontColor: v.fontColor || DEFAULT.fontColor,
    headerColor: v.headerColor || DEFAULT.headerColor,
    visibleRows: Number.isFinite(v.visibleRows) ? v.visibleRows : DEFAULT.visibleRows,
  };
}

// ── grid ops (all pure, all return a NEW normalized value) ────────────────────

export function addRow(value, at) {
  const v = normalize(value);
  if (v.rows.length >= MAX_ROWS) return v;
  const blank = new Array(numCols(v)).fill("");
  const rows = clone2d(v.rows);
  const idx = at == null ? rows.length : Math.max(0, Math.min(rows.length, at));
  rows.splice(idx, 0, blank);
  return { ...v, rows };
}

export function removeRow(value, at) {
  const v = normalize(value);
  if (v.rows.length <= MIN_ROWS) return v; // never drop below 1 row
  const rows = clone2d(v.rows);
  const idx = Math.max(0, Math.min(rows.length - 1, at));
  rows.splice(idx, 1);
  return { ...v, rows };
}

export function addColumn(value, at) {
  const v = normalize(value);
  if (numCols(v) >= MAX_COLS) return v;
  const idx = at == null ? numCols(v) : Math.max(0, Math.min(numCols(v), at));
  const header = v.header.slice();
  header.splice(idx, 0, "");
  const aligns = v.aligns.slice();
  aligns.splice(idx, 0, "left");
  const rows = v.rows.map((r) => {
    const nr = r.slice();
    nr.splice(idx, 0, "");
    return nr;
  });
  return { ...v, header, aligns, rows };
}

export function removeColumn(value, at) {
  const v = normalize(value);
  if (numCols(v) <= MIN_COLS) return v; // never drop below 1 column
  const idx = Math.max(0, Math.min(numCols(v) - 1, at));
  const header = v.header.slice();
  header.splice(idx, 1);
  const aligns = v.aligns.slice();
  aligns.splice(idx, 1);
  const rows = v.rows.map((r) => {
    const nr = r.slice();
    nr.splice(idx, 1);
    return nr;
  });
  return { ...v, header, aligns, rows };
}

export function setHeaderCell(value, col, text) {
  const v = normalize(value);
  if (col < 0 || col >= numCols(v)) return v;
  const header = v.header.slice();
  header[col] = text == null ? "" : String(text);
  return { ...v, header };
}

export function setCell(value, row, col, text) {
  const v = normalize(value);
  if (row < 0 || row >= v.rows.length || col < 0 || col >= numCols(v)) return v;
  const rows = clone2d(v.rows);
  rows[row][col] = text == null ? "" : String(text);
  return { ...v, rows };
}

export function setColumnAlign(value, col, align) {
  const v = normalize(value);
  if (col < 0 || col >= numCols(v)) return v;
  const aligns = v.aligns.slice();
  aligns[col] = ALIGNS.includes(align) ? align : "left";
  return { ...v, aligns };
}

export function setShowHeader(value, show) {
  return { ...normalize(value), showHeader: !!show };
}

export function setField(value, key, val) {
  const v = normalize(value);
  if (["caption", "font", "fontColor", "headerColor"].includes(key)) {
    return { ...v, [key]: val };
  }
  return v;
}

// ── craft.js <-> value adapter ────────────────────────────────────────────────
// The craft node stores the model under a single `table` prop (plus block-level
// margin, mirroring the other blocks). These two are the round-trip contract.
export function nodePropsToValue(props = {}) {
  return normalize(props.table);
}

export function applyValueToProps(value, props = {}) {
  return { ...props, table: normalize(value) };
}

// Render helper: how many rows are visible before the panel scrolls.
export const limits = { MAX_COLS, MAX_ROWS, MIN_COLS, MIN_ROWS };

export default {
  ALIGNS,
  DEFAULT,
  EMPTY,
  normalize,
  addRow,
  removeRow,
  addColumn,
  removeColumn,
  setHeaderCell,
  setCell,
  setColumnAlign,
  setShowHeader,
  setField,
  nodePropsToValue,
  applyValueToProps,
  limits,
};
